import React, { useState, useEffect, useRef } from "react";
import Papa from "papaparse"; // Install with `npm install papaparse`
import { instance2 } from "../../utils/axiosInstance";
import DatePicker from "react-datepicker";
import CalendarIcon from "../../img/calendaricon.svg";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { ReactComponent as BackButton } from "../../img/bacbuttonicon.svg";
import { formatNumber } from "../../numberingformat"; // Ensure formatNumber works for formatting
import { basecode } from "../../mainscreen/pesananscreen/baserobotocode";
import { basecodeBold } from "../pesananscreen/baserobotoboldcode";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function CsvPage() {
  const location_id = localStorage?.getItem("locationId") || null;
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [formData, setFormData] = useState({
    location_id: location_id,
    supplier_id: "",
  });
  const [uploadedData, setUploadedData] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [csvContent, setCsvContent] = useState("");
  const [location, setLocation] = useState([]); // Storage locations
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null); // Ref to track dropdown

  const queryParams = new URLSearchParams(useLocation().search);
  const supplierId = queryParams.get("id");

  // state
  const [supplierList, setSupplierList] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(true);

  // fetch suppliers
  useEffect(() => {
    const fetchSupplierList = async () => {
      try {
        const { data } = await instance2.get(`/supplier`);
        setSupplierList(data || []);
        // set default supplier_id if none set yet
        if (data?.length) {
          setFormData((prev) => ({
            ...prev,
            supplier_id: prev.supplier_id || data[0].id,
          }));
        }
      } catch (err) {
        // handle error if you want: toast / setError
      } finally {
        setSupplierLoading(false);
      }
    };
    fetchSupplierList();
  }, []);

  // derive supplierName from supplier_id
  const supplierName = React.useMemo(() => {
    if (!formData.supplier_id || supplierList.length === 0) return "";
    const match = supplierList.find(
      (s) => String(s.id) === String(formData.supplier_id),
    );
    return match ? match.supplier_name : "";
  }, [formData.supplier_id, supplierList]);

  useEffect(() => {
    const storedUsername =
      localStorage.getItem("userAuthentication") || "Guest";
    setUsername(storedUsername);
  }, []);

  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const response = await instance2.get("/ingredient_master");
        setIngredients(response.data);
      } catch (error) {
        console.error("Failed to fetch ingredients", error);
      }
    };

    fetchIngredients();
  }, []);

  const CustomInput = React.forwardRef(({ onClick, isOpen }, ref) => (
    <button
      className="flex items-center justify-between w-full px-4 py-2 border-none rounded-[15px] bg-white"
      onClick={onClick}
      ref={ref}
      type="button"
    >
      {/* Left Section: Calendar Icon */}
      <div className="flex items-center gap-2">
        <img
          src={CalendarIcon}
          alt="Calendar"
          className="h-5 w-5 text-gray-500"
        />

        {/* Separator */}
        <div className="w-px h-5 bg-gray-300 mx-1 me-3"></div>
        <span>
          {new Date(selectedDate).toLocaleDateString("id-ID", {
            year: "numeric",
            month: "long",
            day: "2-digit",
          })}
        </span>
      </div>

      {/* Right Section: Rotating Arrow */}
      <div
        className={`text-black font-semibold transform transition-transform duration-500 ease-in-out ps-4 ${
          isOpen ? "rotate-180" : "rotate-0"
        }`}
      >
        ▼
      </div>
    </button>
  ));

  const filteredLocations = location.filter((loc) =>
    loc.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await instance2.get("supplier"); // Use instance2 for the API call

        const locationData = response.data; // Access the data property from Axios response

        setLocation(
          locationData.map((loc) => ({
            id: loc.id,
            name: loc.supplier_name,
            code: loc.code,
          })),
        );
      } catch (err) {
        console.error("Failed to fetch locations", err);
        setError(
          err.response?.data?.message || "Failed to fetch storage locations",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, []);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          setUploadedData(results.data);
          await validateIngredients(results.data);
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
        },
      });
    }
  };

  const validateIngredients = async (data) => {
    setLoadingValidation(true);

    const results = {};
    for (const row of data) {
      const ingredientName = row["Bahan Baku"] || row.bahan_baku;
      if (ingredientName) {
        try {
          const response = await instance2.get(
            `/ingredient/name/${encodeURIComponent(ingredientName)}`,
          );
          const status =
            response.data[0].search_ingredient === "OK" ? "OK" : "INVALID";
          results[ingredientName] = status;
        } catch (error) {
          console.error(
            `Error validating ingredient ${ingredientName}:`,
            error,
          );
          results[ingredientName] = "INVALID";
        }
      }
    }

    setValidationResults(results);
    setLoadingValidation(false);
  };

  const postdata = async () => {
    if (!selectedDate) {
      alert("Please ensure all fields are filled and a CSV file is uploaded.");
      return;
    }

    try {
      // Get the file from the file input
      const fileInput = document.querySelector('input[type="file"]');
      const csvFile = fileInput.files[0];

      if (!csvFile) {
        alert("Please upload a CSV file.");
        return;
      }

      const localDateString = (thedate) => {
        const year = thedate.getFullYear();
        const month = String(thedate.getMonth() + 1).padStart(2, "0"); // Adding 1 because months are 0-based
        const day = String(thedate.getDate()).padStart(2, "0");

        const localDateString = `${year}-${month}-${day}`;

        return localDateString;
      };

      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append("startdate", localDateString(selectedDate)); // Format as YYYY-MM-DD
      formDataToSend.append("location_id", formData.location_id); // Add location ID
      formDataToSend.append("admin_id", username); // Add username
      formDataToSend.append("fileUpload", csvFile); // Attach the CSV file

      // Make the API call
      const response = await instance2.post(
        "save_upload_price",
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" }, // Set proper headers
        },
      );
      if (response.status === "OK") {
        generatePdf();
        navigate("/manajemenuploadharga");
        alert("Data successfully posted!");
      } else {
        alert("Failed to post data. Please try again.");
      }
    } catch (error) {
      console.error("Error posting data:", error);
      alert("An error occurred while posting data.", error);
    }
  };

  const handleGenerateExampleCsv = () => {
    if (!ingredients || ingredients.length === 0) {
      alert("Data bahan baku belum tersedia.");
      return;
    }

    // Sort ingredients alphabetically by ingredient_name
    const sortedIngredients = [...ingredients].sort((a, b) =>
      a.ingredient_name.localeCompare(b.ingredient_name),
    );

    const csvHeader = "sep=;\nNo;Bahan Baku;uoq;harga\n";
    const csvRows = sortedIngredients.map((item, index) =>
      [
        index + 1,
        item.ingredient_name || "-",
        item.uoq_name || "-",
        "", // Let 'harga' be empty for user to fill in manually
      ].join(";"),
    );

    const csvData = csvHeader + csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Contoh_Harga_Bahan_Baku.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCsv = () => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "validated_data.csv");
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if all data is valid
  const isAllValid = () =>
    uploadedData.every((row) => {
      const ingredientName = row["Bahan Baku"] || row.bahan_baku;
      return validationResults[ingredientName] === "OK";
    });

  // Build rows exactly like your rendered table (valid rows only)
  const getValidatedRows = () => {
    let rows = [];
    let rowNumber = 1;

    uploadedData.forEach((row) => {
      const ingredientName = row["Bahan Baku"] || row.bahan_baku;
      const status = validationResults[ingredientName];
      const harga = row.harga;

      if (!harga || isNaN(harga) || Number(harga) === 0) return;

      if (status === "OK") {
        rows.push([
          `${rowNumber++}.`,
          ingredientName || "-",
          row.uoq || "-",
          `Rp. ${formatNumber(row.harga) || "-"}`,
        ]);
      }
    });

    return rows;
  };

  const generatePdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 40;
    const pageCount = doc.internal.getNumberOfPages();
    
    // Fonts
    doc.addFileToVFS("Roboto-Regular.ttf", basecode);
    doc.addFileToVFS("Roboto-Bold.ttf", basecodeBold);
    doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
    doc.setFont("Roboto", "normal");

    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ?? pageSize.getWidth();

    const title = "Daftar Harga Bahan Baku";
    const dateStr = selectedDate
      ? new Date(selectedDate).toLocaleDateString("id-ID", {
          year: "numeric",
          month: "long",
          day: "2-digit",
        })
      : "-";

    doc.setFont("Roboto", "bold");
    doc.setFontSize(14);
    doc.text(title, pageWidth / 2, 60, { align: "center" });

    doc.setFont("Roboto", "normal");
    doc.setFontSize(10);
    doc.text(`Tanggal: ${dateStr}`, margin + 10, 100);

    const head = [["No.", "Bahan Baku", "UoQ", "Harga Bahan"]];
    const body = getValidatedRows();

    if (body.length === 0) {
      doc.text("Tidak ada baris valid untuk dicetak.", margin + 10, 120);
    } else {
      const tableWidth = pageWidth - 2 * (margin + 10);
      const fixedColsWidth = 40 + 60 + 100;
      const bahanBakuColWidth = tableWidth - fixedColsWidth;

      doc.autoTable({
        head,
        body,
        startY: 110,
        margin: { left: margin + 10, right: margin + 10 },
        styles: { font: "Roboto", fontSize: 9, cellPadding: 6 },
        headStyles: { fillColor: [1, 24, 70] },
        columnStyles: {
          0: { halign: "left" }, // No.
          1: { cellWidth: bahanBakuColWidth }, // Bahan baku
          2: { halign: "center", cellWidth: 60 }, // UoQ
          3: { halign: "right", cellWidth: 100 }, // Harga
        },
        didParseCell: (data) => {
          if (
            data.section === "head" &&
            (data.column.index === 2 || data.column.index === 3)
          ) {
            data.cell.styles.halign = "center";
          }
        },
        didDrawPage: (data) => {
          const pageInfo = doc.internal.getCurrentPageInfo();
          const pageHeight = pageSize.height ?? pageSize.getHeight();

          const footerText = `Hal. ${pageInfo.pageNumber} / ${pageCount}`;
          doc.setFontSize(9);
          doc.text(footerText, pageWidth - (margin + 10), pageHeight - 10, {
            align: "right",
          });
        },
      });

      const pageHeight = pageSize.height ?? pageSize.getHeight();
      const finalY = doc.lastAutoTable?.finalY ?? 110;

      const availableBottom = pageHeight - margin - 30;
      const sigY = Math.min(finalY + 130, availableBottom);
      const sigLineWidth = 160;
      const sigX2 = pageWidth - (margin + 10);
      const sigX1 = sigX2 - sigLineWidth;

      doc.setFontSize(10);
      doc.text("Disetujui Oleh,", sigX1, sigY - 80);

      doc.setLineWidth(0.5);
      doc.line(sigX1, sigY, sigX2, sigY);

      doc.text(supplierName, (sigX1 + sigX2) / 2, sigY + 20, {
        align: "center",
      });
    }

    const fileDate = selectedDate
      ? new Date(selectedDate).toISOString().slice(0, 10)
      : "tanggal";
    const fileSupp = (supplierName || "").replace(/[^\w\-]+/g, "_");
    doc.save(`Harga_Valid_${fileSupp}_${fileDate}.pdf`);
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="flex flex-row gap-4 items-center">
        <div
          className="bg-white p-2 rounded-full cursor-pointer"
          onClick={() => window.history.back()}
        >
          <BackButton className="rotate-180 text-black" />
        </div>
        <h1 className="block text-black text-xl sm:text-2xl my-2 font-medium">
          Upload CSV
        </h1>
      </div>

      <div className="flex flex-col gap-4 items-left py-4">
        <div className="flex flex-row gap-4 items-center">
          <div className="w-1/5">Tanggal</div>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="yyyy-MM-dd"
            // filterDate={(date) => {
            // const day = date.getDay();
            // return day !== 0 && day !== 6;
            // }}
            onCalendarOpen={() => setIsOpen(true)}
            onCalendarClose={() => setIsOpen(false)}
            customInput={<CustomInput isOpen={isOpen} />}
          />
        </div>
        <div className="flex flex-row gap-4 items-center">
          <div className="w-1/5">Pemasok</div>
          <div className="relative">
            <select
              className="flex items-center justify-between w-full py-2 border-none rounded-[15px] bg-white px-4 pe-8"
              value={formData.supplier_id ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  supplier_id: Number(e.target.value),
                }))
              }
              disabled={supplierLoading || supplierList.length === 0}
            >
              {supplierLoading && <option>Memuat pemasok...</option>}
              {!supplierLoading && supplierList.length === 0 && (
                <option>Tidak ada pemasok</option>
              )}
              {!supplierLoading &&
                supplierList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.supplier_name}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <>
          {/* Location ID */}
          <div className="relative" ref={dropdownRef}>
            {/* <label className="block text-gray-700 mb-2">Pemasok</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Pilih pemasok"
                value={supplierId? supplierParam[0].supplier_name : searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsDropdownOpen(true)}
                className="w-full px-3 border-none rounded-full bg-white text-xs py-4 focus:outline-none"
              />
              <div
                className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
              >
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div> */}
            {isDropdownOpen && (
              <div className="absolute z-10 bg-white border rounded-lg w-full max-h-40 overflow-y-auto shadow-lg mt-1">
                {filteredLocations.length > 0 ? (
                  filteredLocations.map((loc) => (
                    <div
                      key={loc.id}
                      className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setFormData({ ...formData, location_id: loc.id });
                        setSearchTerm(loc.name);
                        setIsDropdownOpen(false);
                      }}
                    >
                      {loc.name}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">
                    No locations found
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Generate Example CSV */}
      <div className="">
        <button
          onClick={handleGenerateExampleCsv}
          className="px-4 py-2 bg-oren-no hover:bg-oren-hover text-white rounded"
        >
          Download Contoh File CSV
        </button>
      </div>

      {/* File Upload */}
      <div className="mb-4">
        <label className="block text-gray-700 mb-2">Upload CSV File</label>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      {/* Display Uploaded Data */}
      {uploadedData.length > 0 && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-2">Data dalam file CSV</h2>
          <table className="w-full border-collapse border-b border-gray-300 table-fixed">
            <thead>
              <tr className="bg-white">
                <th className="w-[25px] lg:w-[50px] border-b border-gray-300 px-4 py-2 bg-gray-100 bg-white rounded-tl-[15px]">
                  No.
                </th>
                <th className="border-b border-gray-300 px-4 py-2 bg-gray-100 bg-white">
                  Bahan Baku
                </th>
                <th className="border-b border-gray-300 px-4 py-2 bg-gray-100 bg-white text-center">
                  uoq
                </th>
                <th className="border-b border-gray-300 px-4 py-2 bg-gray-100 bg-white">
                  Harga
                </th>
                <th className="border-b border-gray-300 px-4 py-2 bg-gray-100 bg-white rounded-tr-[15px]">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let rowNumber = 1;
                return uploadedData.map((row, index) => {
                  const ingredientName = row["Bahan Baku"] || row.bahan_baku;
                  const status = validationResults[ingredientName];
                  const harga = row.harga;

                  // Skip invalid rows
                  if (!harga || isNaN(harga) || Number(harga) === 0)
                    return null;

                  // Render only valid rows and increase counter
                  return (
                    <tr key={index} className="odd:bg-gray-100 even:bg-white">
                      <td className="w-[25px] lg:w-[50px] border-b border-gray-300 px-4 py-2 text-sm">
                        {rowNumber++}
                      </td>
                      <td className="border-b border-gray-300 px-4 py-2 text-sm">
                        {ingredientName || "-"}
                      </td>
                      <td className="border-b border-gray-300 px-4 py-2 text-sm text-center">
                        {row.uoq}
                      </td>
                      <td className="border-b border-gray-300 px-4 py-2 text-sm text-right">
                        {formatNumber(row.harga) || "-"}
                      </td>
                      <td className="border-b border-gray-300 px-4 py-2 text-sm text-center">
                        {status === "OK" && (
                          <span className="text-green-500">Valid</span>
                        )}
                        {status === "INVALID" && (
                          <span className="text-red-500">Tidak Valid</span>
                        )}
                        {!status && (
                          <span className="text-gray-500">Memvalidasi...</span>
                        )}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>

          {/* Generate and Download CSV */}
          <div className="flex flex-col gap-4 p-4">
            <button
              onClick={postdata}
              className={`px-4 py-2 ${
                isAllValid() && selectedDate
                  ? "bg-merah-no hover:bg-blue-600"
                  : "bg-gray-400 cursor-not-allowed"
              } text-white rounded-[25px] py-2`}
              disabled={
                !(
                  isAllValid() ||
                  !formData.location_id ||
                  !formData.location_id === null ||
                  !selectedDate
                )
              }
            >
              Simpan dan download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
